import { useMemo, useState } from 'react';
import { Eye, Pencil, X } from 'lucide-react';
import {
  MAIL_PREVIEW_SAMPLE_VARS,
  applyMailPlaceholders,
  buildMailPreviewParts,
} from '../utils/mailPreview';

/**
 * Recipient-style mail preview. Edits sync back to Mail settings (header/footer/subject/body).
 */
export default function MailRecipientPreviewModal({
  open,
  onClose,
  templateLabel = 'Template',
  fromAddress = '',
  replyTo = '',
  subject,
  headerText,
  body,
  footerText,
  onChangeSubject,
  onChangeHeader,
  onChangeBody,
  onChangeFooter,
  sampleVars = MAIL_PREVIEW_SAMPLE_VARS,
}) {
  const [mode, setMode] = useState('recipient'); // recipient | edit

  const filled = useMemo(
    () =>
      buildMailPreviewParts({
        subject,
        headerText,
        body,
        footerText,
        vars: sampleVars,
        filled: true,
      }),
    [subject, headerText, body, footerText, sampleVars]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Mail preview — {templateLabel}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Recipient view uses sample data (e.g. {sampleVars.ss_label}). Switch to Edit to change
              templates — changes update Mail settings immediately (then Save on the page).
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-2 border-b flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setMode('recipient')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
              mode === 'recipient'
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Recipient view
          </button>
          <button
            type="button"
            onClick={() => setMode('edit')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
              mode === 'edit'
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit templates
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-slate-100">
          <div className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 space-y-2 text-sm">
              <div className="flex gap-2">
                <span className="w-14 shrink-0 text-xs font-semibold text-slate-500 pt-0.5">From</span>
                <span className="text-slate-800 break-all">{fromAddress || '—'}{replyTo ? ` (reply-to: ${replyTo})` : ''}</span>
              </div>
              <div className="flex gap-2">
                <span className="w-14 shrink-0 text-xs font-semibold text-slate-500 pt-0.5">To</span>
                <span className="text-slate-600">recipient@example.com</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="w-14 shrink-0 text-xs font-semibold text-slate-500 pt-2">Subject</span>
                {mode === 'edit' ? (
                  <input
                    type="text"
                    value={subject || ''}
                    onChange={(e) => onChangeSubject?.(e.target.value)}
                    className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm font-medium"
                  />
                ) : (
                  <span className="flex-1 font-semibold text-slate-900 pt-1.5">{filled.subject || '(no subject)'}</span>
                )}
              </div>
            </div>

            {mode === 'recipient' ? (
              <div className="px-4 py-4 space-y-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                {filled.header ? (
                  <div className="pb-3 border-b border-slate-100 text-slate-600 text-[13px]">{filled.header}</div>
                ) : null}
                <div>{filled.body || <span className="text-slate-400 italic">(empty body)</span>}</div>
                {filled.footer ? (
                  <div className="pt-3 border-t border-slate-100 text-slate-500 text-[12px]">{filled.footer}</div>
                ) : null}
                <div className="mt-4 rounded border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                  Attachment: {applyMailPlaceholders('{{file_name}}', sampleVars)}
                </div>
              </div>
            ) : (
              <div className="px-4 py-4 space-y-4">
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-600">Header (letterhead)</span>
                  <textarea
                    rows={3}
                    value={headerText || ''}
                    onChange={(e) => onChangeHeader?.(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-600">Body</span>
                  <textarea
                    rows={10}
                    value={body || ''}
                    onChange={(e) => onChangeBody?.(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-xs font-semibold text-slate-600">Footer</span>
                  <textarea
                    rows={3}
                    value={footerText || ''}
                    onChange={(e) => onChangeFooter?.(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                  />
                </label>
                <p className="text-[11px] text-slate-500">
                  Use placeholders such as {'{{building_name}}'}, {'{{ss_label}}'}, {'{{issued_date}}'}.
                  Switch to Recipient view to see sample fill-in.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold"
          >
            Done — back to settings
          </button>
        </div>
      </div>
    </div>
  );
}
